import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Users,
  Scale,
  Heart,
  Loader2,
  CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactReasons = [
  { id: 'soutien_structure', label: 'Demande de soutien structuré' },
  { id: 'accompagnement', label: 'Accompagnement juridique' },
  { id: 'adhesion', label: 'Adhésion au collectif' },
  { id: 'partenariat', label: 'Proposition de partenariat' },
  { id: 'presse', label: 'Demande presse / média' },
  { id: 'autre', label: 'Autre demande' }
];

export default function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    reason: 'soutien_structure',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success('Message envoyé ! Nous vous répondrons dans les meilleurs délais.');
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" data-testid="contact-page">
        <div className="bg-muted/30 py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Message envoyé !
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              Merci pour votre message. Notre équipe vous répondra dans les meilleurs délais.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/')}>
                Retour à l'accueil
              </Button>
              <Button variant="outline" onClick={() => {
                setSubmitted(false);
                setForm({
                  name: user?.name || '',
                  email: user?.email || '',
                  phone: '',
                  reason: 'soutien_structure',
                  subject: '',
                  message: ''
                });
              }}>
                Envoyer un autre message
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Header */}
      <div className="bg-muted/30 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Contacter le Collectif
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Vous avez besoin d'un soutien structuré, d'un accompagnement juridique ou vous souhaitez 
            rejoindre notre collectif ? N'hésitez pas à nous contacter.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Notre mission
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Le Collectif de défense de l'habitat léger accompagne et soutient les personnes 
                  confrontées à des difficultés administratives ou juridiques liées à leur mode de vie 
                  en habitat léger.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-primary" />
                  Nos services
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span className="text-sm">Accompagnement juridique personnalisé</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span className="text-sm">Mise en réseau avec des avocats spécialisés</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span className="text-sm">Soutien collectif et solidaire</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span className="text-sm">Formations et ateliers</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-primary" />
                  Nous soutenir
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Votre soutien nous permet de continuer notre mission d'accompagnement.
                </p>
                <a
                  href="https://www.helloasso.com/associations/desobeissance-fertile/formulaires/1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="w-full gap-2">
                    <Heart className="w-4 h-4" />
                    Faire un don
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Formulaire de contact</CardTitle>
                <CardDescription>
                  Remplissez ce formulaire et nous vous répondrons dans les meilleurs délais.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        placeholder="Votre nom"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        placeholder="votre@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="06 12 34 56 78"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reason">Motif de contact *</Label>
                      <Select
                        value={form.reason}
                        onValueChange={(v) => setForm({ ...form, reason: v })}
                      >
                        <SelectTrigger id="reason" data-testid="contact-reason-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {contactReasons.map((reason) => (
                            <SelectItem key={reason.id} value={reason.id}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Objet *</Label>
                    <Input
                      id="subject"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      required
                      placeholder="Résumez votre demande en quelques mots"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      rows={6}
                      placeholder="Décrivez votre situation et votre demande de manière détaillée..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg text-sm text-muted-foreground">
                    <p>
                      En soumettant ce formulaire, vous acceptez que vos données soient utilisées 
                      pour traiter votre demande. Nous ne partagerons jamais vos informations avec des tiers.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full gap-2" 
                    size="lg"
                    disabled={loading}
                    data-testid="contact-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
